import React, {useEffect, useState} from "react";
import s from './SoundDemoPrompt.module.scss';
import {GenericModal} from "../../common/genericModal/GenericModal";
import Checkbox from "../../common/tools/Checkbox/Checkbox";
import Button from "../../common/Button";
import {lessonPlayerStore} from "../../../redux/lesson/LessonPlayerStore";
import {useAppSelector} from "../../../redux/hooks";
import {usePitchDetectionStatus} from "../../../hooks/pitchDetectionStatus.hook";
import {iconImages} from "../../../config/config";
import {settingsStore} from "../../../redux/settings/settingsStore";
import {defaultVolumes} from "../../../redux/settings/settingsDefaults";

export default function SoundDemoPrompt() {
  const frequency: number = 30;
  const segmentsOpenedCount = useAppSelector(lessonPlayerStore.getMetricsSegmentsOpenedCount);
  const isDemoSoundPromptAllowedByUser = useAppSelector(lessonPlayerStore.getDemoSoundPromptIsAllowedByUser);
  const isDemoModeOff = !lessonPlayerStore.getIsDemoLeftHandSoundEnabled() && !lessonPlayerStore.getIsDemoRightHandSoundEnabled();
  const {isPitchDetectionEnabled} = usePitchDetectionStatus();
  const isCountAMultipleOfFrequency = segmentsOpenedCount % frequency === 0;
  const isCountNotZero = segmentsOpenedCount !== 0;
  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    setIsOpen(isDemoSoundPromptAllowedByUser && isCountAMultipleOfFrequency && isCountNotZero && isDemoModeOff);
  }, [isDemoSoundPromptAllowedByUser, isCountAMultipleOfFrequency, isCountNotZero, isDemoModeOff]);

  if (!isOpen) {
    return null;
  }

  let localIsDemoSoundPromptAllowedByUser: boolean = isDemoSoundPromptAllowedByUser;

  return (
    <GenericModal
      canShow={(): boolean => {
        return isOpen;
      }}
      backgroundColor={"white"}
      title={"Want to hear how it should sound?"}
      onClose={() => {
        setIsOpen(false);
        lessonPlayerStore.setDemoSoundPromptIsAllowedByUser(localIsDemoSoundPromptAllowedByUser);
      }}
    >
      <div className={s.container}>
        <div className={s.topText}>
          <p><strong>Piano sound demos</strong> let you hear how the music should sound, a great learning feature.</p>
          { isPitchDetectionEnabled
            ?
            <p>
              <small><strong>Turning piano demos on disables mic scoring.</strong> You can re-enable scoring by turning the demo sounds off.</small>
            </p>
            : null
          }
        </div>

        <div className={s.buttonSection}>
          { isPitchDetectionEnabled
            ?
            <>
              <Button
                buttonType={`secondary`}
                text={"Keep Mic On"}
                onClick={() => {
                  // Increment the segment counter so prompt goes away
                  lessonPlayerStore.incrementMetricsSegmentsOpenedCount();
                }}>
              </Button>
            </>

            : null
          }
          <Button
            text={
              <>
                <img
                  src={iconImages.handsOnKeyboard}
                  alt={"Hands on piano"}
                  style={{width: "24px", height: "24px"}}
                />
                <span style={{ marginLeft: "6px"}}>Turn On Sound Demos</span>
              </>
            }
            onClick={() => {
              if (settingsStore.getPianoVolume() === 0) {
                settingsStore.setPianoVolume(defaultVolumes.piano);
              }

              lessonPlayerStore.setIsDemoRightHandSoundEnabled(true);
              lessonPlayerStore.setIsDemoLeftHandSoundEnabled(true);
              // Increment the segment counter so prompt won't show again if user immediately turns off piano sound demos.
              lessonPlayerStore.incrementMetricsSegmentsOpenedCount();
            }}
            buttonType={`primary`}>
          </Button>
        </div>

        <div className={s.checkBoxSection}>
          <Checkbox
            name={`enableSoundDemoCheckbox`}
            isChecked={!localIsDemoSoundPromptAllowedByUser}
            label={`Don't show this again`}
            onChange={(isChecked: boolean) => {
              localIsDemoSoundPromptAllowedByUser = !isChecked;
            }}
          />
        </div>
      </div>
    </GenericModal>
  );
}